import { useState } from "react";
import { motion } from "framer-motion";
import { X, Wand2, Users, Crown, Check } from "lucide-react";
import { toast } from "sonner";
import { createCheckout } from "@/lib/api";
import { sfx } from "@/lib/sounds";

const OPTIONS = [
  {
    lookup: "autoline_one_time",
    title: "Auto Line Art",
    price: 10,
    icon: Wand2,
    desc: "Turn any photo into a coloring page with one tap",
    testid: "paywall-autoline",
    owned: (p) => p.includes("autoline"),
  },
  {
    lookup: "family_one_time",
    title: "Family Sharing",
    price: 10,
    icon: Users,
    desc: "Share pages, gallery & rewards with up to 6 family devices",
    testid: "paywall-family",
    owned: (p) => p.includes("family"),
  },
  {
    lookup: "full_access_one_time",
    title: "Full Access",
    price: 20,
    icon: Crown,
    desc: "Every premium feature unlocked forever — best value",
    testid: "paywall-bundle",
    best: true,
    owned: (p) => p.includes("autoline") && p.includes("family"),
  },
];

export default function PaywallModal({ owned = [], onClose }) {
  const [busy, setBusy] = useState(null);

  const pay = async (lookup) => {
    setBusy(lookup);
    sfx.click();
    try {
      const res = await createCheckout(lookup, window.location.origin);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Checkout didn't open — try again");
      setBusy(null);
    }
  };

  return (
    <motion.div
      data-testid="paywall-modal"
      className="fixed inset-0 z-[92] flex items-center justify-center bg-ink/85 backdrop-blur-md px-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.7, y: 40, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-3xl border-2 border-pumpkin/50 bg-night p-6 shadow-frame sm:p-8"
      >
        <button
          data-testid="paywall-close-btn"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full border border-white/15 p-2 text-paper/70 transition-colors hover:border-candy hover:text-candy"
        >
          <X size={16} />
        </button>
        <div className="font-display text-4xl text-pumpkin text-glow-orange sm:text-5xl">
          Unlock the Magic
        </div>
        <p className="mt-2 text-sm text-paper/65">
          One-time payment. No subscription, no tricks — just treats.
        </p>

        <div className="mt-6 grid gap-3">
          {OPTIONS.map((o) => {
            const has = o.owned(owned);
            const Icon = o.icon;
            return (
              <button
                key={o.lookup}
                data-testid={o.testid}
                disabled={has || busy !== null}
                onClick={() => pay(o.lookup)}
                className={`flex items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all ${
                  has
                    ? "border-slime/40 bg-slime/10 opacity-80"
                    : o.best
                      ? "border-slime/60 bg-slime/5 hover:shadow-glow-slime hover:scale-[1.02]"
                      : "border-white/15 bg-white/5 hover:border-pumpkin/60 hover:scale-[1.02]"
                } disabled:cursor-default`}
              >
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                    o.best ? "bg-slime text-ink" : "bg-pumpkin text-ink"
                  }`}
                >
                  <Icon size={20} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 font-semibold text-paper">
                    {o.title}
                    {o.best && !has && (
                      <span className="rounded-full bg-slime px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ink">
                        Best value
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs leading-snug text-paper/55">{o.desc}</span>
                </span>
                {has ? (
                  <span className="flex items-center gap-1 text-sm font-bold text-slime">
                    <Check size={16} /> Owned
                  </span>
                ) : (
                  <span className="font-display text-3xl text-pumpkin">
                    {busy === o.lookup ? "..." : `$${o.price}`}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className="mt-5 text-center text-[11px] text-paper/40">
          Secure checkout via Stripe · One-time payment · Card, Apple Pay & Google Pay
        </p>
      </motion.div>
    </motion.div>
  );
}
